import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation, useParams, useSearchParams } from "react-router-dom";
import {
  ArrowLeft, BookOpen, CheckSquare, ChevronRight, Clock, Dumbbell, Info, ListOrdered,
  Send, ShieldCheck, ShieldQuestion, Sparkles, Star, Target, TriangleAlert, Utensils,
} from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "../components/ui/accordion";
import FieldDiagram from "../components/FieldDiagram";
import LoadFailureNote from "../components/LoadFailureNote";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { safeRedirectTarget } from "../lib/redirectTarget";
import { assignGuide, fetchAssignedGuides, fetchGuideForPage, publishGuide } from "../lib/guides";
import { toast } from "sonner";
import LearnChrome, { useLearnSectionsLook } from "../components/LearnChrome";
import { learnTopicHref } from "../lib/learnSection";
import CoachGuideAssignPanel from "../components/CoachGuideAssignPanel";

const NAVY = "#0B2E59", GOLD = "#D4A017";

function fmtWhen(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return isNaN(d.getTime()) ? "" : d.toLocaleDateString("en-NZ");
}

// #1125: the nested render sits OUTSIDE `Block`'s own body. A component that
// names itself in JSX inside its own function overflows the stack in this
// repo's jest transform ("Maximum call stack size exceeded" while compiling
// the file), which made the whole page impossible to mount in a unit test.
// Same output, same keys; only where the self-reference is written moves.
const renderNestedBlock = (b, i) => <Block key={i} block={b} />;

/** A single labelled prose/list block (guide.sections[].blocks[]) — the
 * generic renderer #220's schema docstring requires: a renderer MUST
 * tolerate a block/section type it has never seen (unknown kinds render
 * nothing rather than crash the page). */
function Block({ block }) {
  if (block.kind === "para") {
    // #474: the measure cap lives on the PARAGRAPH, never on a container —
    // `max-w-prose` here is what lets the page frame go full width without
    // the body text stretching to ~95+ characters a line.
    return <p className="text-sm text-slate-700 leading-relaxed max-w-prose">{block.text}</p>;
  }
  if (block.kind === "list") {
    // #391: an `ordered: true` list is a numbered sequence from the source —
    // a fitness drill's INSTRUCTIONS are steps 1..n, and rendering them as
    // bullets would lose the order a child is meant to follow.
    if (block.ordered) {
      return (
        <ol className="space-y-1.5 list-decimal list-inside">
          {block.items.map((item, i) => (
            <li key={i} className="text-sm text-slate-700 leading-relaxed">{item}</li>
          ))}
        </ol>
      );
    }
    return (
      <ul className="space-y-1.5">
        {block.items.map((item, i) => (
          <li key={i} className="text-sm text-slate-700 flex gap-2 leading-relaxed">
            <span className="w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0" style={{ background: NAVY }} />
            {item}
          </li>
        ))}
      </ul>
    );
  }
  if (block.kind === "point") {
    return (
      <div className="space-y-1.5">
        <div className="font-semibold text-sm text-slate-800">{block.label}</div>
        <div className="pl-3 space-y-1.5 border-l-2 border-slate-100">
          {block.blocks.map(renderNestedBlock)}
        </div>
      </div>
    );
  }
  return null;
}

/** `symptom → consequence` (issue #220): the pair is the point, but
 * `consequence` is genuinely null for one continuous source clause — so
 * this must not look broken with only a symptom. No placeholder text for
 * the missing half; the arrow itself simply doesn't render. */
function MistakePair({ symptom, consequence, testId }) {
  return (
    <div className="rounded-xl border border-slate-200 p-4" data-testid={testId}>
      <div className="flex gap-2">
        <TriangleAlert size={16} className="text-amber-600 shrink-0 mt-0.5" />
        <p className="text-sm font-semibold text-slate-900 leading-snug">{symptom}</p>
      </div>
      {consequence && (
        <div className="flex gap-2 mt-2 pl-6">
          <ChevronRight size={14} className="text-slate-400 shrink-0 mt-0.5" />
          <p className="text-sm text-slate-600 leading-snug">{consequence}</p>
        </div>
      )}
    </div>
  );
}

/** `failure → why → correction` — same null-tolerant shape as MistakePair,
 * one field further (issue #220's "same null caveat as above"). */
function ErrorTriple({ failure, why, correction, testId }) {
  return (
    <div className="rounded-xl border border-slate-200 p-4 space-y-1.5" data-testid={testId}>
      <p className="text-sm font-semibold text-slate-900 leading-snug">{failure}</p>
      {why && <p className="text-sm text-slate-600 leading-snug">{why}</p>}
      {correction && (
        <p className="text-sm font-medium leading-snug" style={{ color: "#15803D" }}>
          Fix: {correction}
        </p>
      )}
    </div>
  );
}

/**
 * Issue #481 — assign THIS guide to one or many of the coach's players,
 * from the page where the coach just read it (Martin, 2026-08-25).
 *
 * This is the SECOND write surface for the same `POST /learn/assignments`
 * upsert as the Coach Portal's Guides-tab picker (CoachPortal.jsx, #382) —
 * same `assignGuide` call, same server gates. Nothing here is the control:
 * the API refuses an unpublished guide and any player the caller does not
 * hold an ACTIVE link with, regardless of what this panel posts
 * (`api/test/guideAssignments.test.ts`). Hiding the panel for a player, or
 * on an unpublished guide, is a courtesy so nobody is offered a button
 * that 404s — the enforcement lives server-side.
 *
 * Decisions recorded on the issue and honoured here:
 *  - **D1** — N independent posts; on partial failure the successes STAND
 *    (the upsert makes a retry safe) and the failures are NAMED inline.
 *  - **D2** — N `assignGuide` calls, no batch endpoint.
 *  - **D3** — players already holding the guide render TICKED, so a
 *    re-assign (which re-stamps `assigned_at` and re-notifies the player,
 *    api/src/routes/notifications.ts) is a deliberate untick-and-re-tick,
 *    never a side effect of not knowing. A tick that was ticked on load
 *    posts nothing; unticking alone removes nothing either — unassign
 *    stays the Coach Portal's job.
 */
function GuideAssignPanel({ category, slug }) {
  const [roster, setRoster] = useState(null); // null = still loading
  const [rosterFailed, setRosterFailed] = useState(false);
  const [assignedIds, setAssignedIds] = useState(() => new Set());
  const [tickedIds, setTickedIds] = useState(() => new Set());
  const [retickedIds, setRetickedIds] = useState(() => new Set());
  const [assigning, setAssigning] = useState(false);
  const [failedNames, setFailedNames] = useState([]);
  const [loadKey, setLoadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setRosterFailed(false);
    // Same roster call CoachPortal.jsx makes: a coach gets their ACTIVE
    // links, an admin gets every player. The per-player assigned lookups
    // (D3's ticked state) are fanned out and best-effort: a player whose
    // lookup fails simply renders unticked — the worst that buys is a
    // deliberate-looking re-assign, which the upsert absorbs.
    api
      .get("/players")
      .then(async (r) => {
        const players = r.data;
        const settled = await Promise.allSettled(
          players.map((p) => fetchAssignedGuides(p.player_id)),
        );
        if (cancelled) return;
        const assigned = new Set();
        settled.forEach((res, i) => {
          if (res.status === "fulfilled" && res.value.some((a) => a.category === category && a.slug === slug)) {
            assigned.add(players[i].player_id);
          }
        });
        setRoster(players);
        setAssignedIds(assigned);
        setTickedIds(new Set(assigned));
        setRetickedIds(new Set());
      })
      .catch(() => {
        if (!cancelled) setRosterFailed(true);
      });
    return () => { cancelled = true; };
  }, [category, slug, loadKey]);

  const toggle = (playerId) => {
    const turningOn = !tickedIds.has(playerId);
    const next = new Set(tickedIds);
    if (turningOn) next.add(playerId);
    else next.delete(playerId);
    setTickedIds(next);
    // D3: an assigned player ticked BY THE COACH (not by the load) is a
    // deliberate re-assign.
    if (turningOn && assignedIds.has(playerId)) {
      setRetickedIds(new Set(retickedIds).add(playerId));
    }
  };

  // Who a press of the button actually posts for: every ticked player who
  // either doesn't have the guide yet, or was deliberately re-ticked.
  const targets = (roster || []).filter(
    (p) => tickedIds.has(p.player_id) && (!assignedIds.has(p.player_id) || retickedIds.has(p.player_id)),
  );

  const assignTargets = async () => {
    setAssigning(true);
    setFailedNames([]);
    // D1/D2: N independent posts, successes stand, failures named.
    const results = await Promise.allSettled(
      targets.map((p) => assignGuide(p.player_id, category, slug)),
    );
    const ok = targets.filter((_, i) => results[i].status === "fulfilled");
    const failed = targets.filter((_, i) => results[i].status !== "fulfilled");
    if (ok.length > 0) {
      const nextAssigned = new Set(assignedIds);
      const nextReticked = new Set(retickedIds);
      ok.forEach((p) => { nextAssigned.add(p.player_id); nextReticked.delete(p.player_id); });
      setAssignedIds(nextAssigned);
      setRetickedIds(nextReticked);
      toast.success(ok.length === 1 ? `Assigned to ${ok[0].full_name}` : `Assigned to ${ok.length} players`);
    }
    // Failed players stay ticked and stay targets, so the button is the
    // retry. Named inline, not only toasted — a toast is gone before a
    // coach at a ground has read past the first name.
    setFailedNames(failed.map((p) => p.full_name));
    setAssigning(false);
  };

  if (rosterFailed) {
    return (
      <Card className="border-slate-200" data-testid="guide-assign-panel">
        <CardContent className="p-5 flex">
          <LoadFailureNote what="your players" onRetry={() => setLoadKey((k) => k + 1)} testId="guide-assign-load-failed" />
        </CardContent>
      </Card>
    );
  }

  if (roster === null) return null; // loading — same no-spinner pattern as the page itself

  return (
    <Card className="border-slate-200" data-testid="guide-assign-panel">
      <CardContent className="p-5 space-y-4">
        <div>
          <h2 className="font-display font-bold text-lg text-slate-900">Assign this guide</h2>
          <p className="text-sm text-slate-500 mt-0.5">
            Your players see it on Training &amp; Guides, with your name on it.
          </p>
        </div>

        {roster.length === 0 ? (
          // #284's rule: this claim renders only once the roster has
          // actually come back — a failed load lands in the branch above.
          <p className="text-sm text-slate-500" data-testid="guide-assign-empty">
            No linked players to assign this guide to yet.
          </p>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-1.5" data-testid="guide-assign-roster">
              {roster.map((p) => (
                <label
                  key={p.player_id}
                  className="flex items-center gap-2.5 rounded-xl border border-slate-200 px-3 py-2.5 cursor-pointer min-w-0"
                >
                  <Checkbox
                    checked={tickedIds.has(p.player_id)}
                    onCheckedChange={() => toggle(p.player_id)}
                    className="shrink-0"
                    data-testid={`guide-assign-player-${p.player_id}`}
                  />
                  <span className="text-sm text-slate-800 min-w-0 flex-1 truncate">{p.full_name}</span>
                  {assignedIds.has(p.player_id) && (
                    <Badge variant="outline" className="text-[10px] shrink-0" data-testid={`guide-assigned-badge-${p.player_id}`}>
                      Assigned
                    </Badge>
                  )}
                </label>
              ))}
            </div>

            {assignedIds.size > 0 && (
              <p className="text-xs text-slate-500" data-testid="guide-assign-reassign-hint">
                Ticked players already have this guide. Unticking won't remove it — untick and
                re-tick one to send it (and its notification) again.
              </p>
            )}

            {failedNames.length > 0 && (
              <p className="text-sm text-red-600" data-testid="guide-assign-failures">
                Couldn't assign to {failedNames.join(", ")}. They're still ticked — press Assign to try again.
              </p>
            )}

            <Button
              onClick={assignTargets}
              disabled={targets.length === 0 || assigning}
              className="w-full sm:w-auto h-11 sm:h-9"
              style={{ background: NAVY }}
              data-testid="guide-assign-btn"
            >
              <Send size={14} className="mr-1.5" />
              {assigning
                ? "Assigning…"
                : targets.length === 1
                  ? `Assign to ${targets[0].full_name}`
                  : targets.length > 1
                    ? `Assign to ${targets.length} players`
                    : "Assign"}
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Issue #391 — Martin's decision (2026-08-22, via Discord): all 43
 * non-technique guides ship, and every guide in the three affected
 * categories carries a CATEGORY-LEVEL DISCLOSURE. It renders ABOVE the
 * content — his decision's own terms: it names who wrote it, who reviewed
 * it, what it is not, and who to ask instead. A generic "general advice"
 * banner is not what that decision bought, so the copy is per category and
 * names the professional class for nutrition and fitness.
 *
 * Keyed by CATEGORY, not guide_type: fitness holds two guide types and both
 * carry the same disclosure. Technique categories have no entry and render
 * no banner — that absence is pinned by GuidePage.test.js.
 */
const CATEGORY_DISCLOSURES = {
  mindset: {
    body: "This guide is AI-drafted general guidance on cricket mindset — not personal or mental-health advice.",
    ask: "If nerves, pressure or setbacks are weighing on you beyond cricket, talk to a parent or caregiver, your coach, your GP, or a counsellor.",
  },
  nutrition: {
    body: "This guide is AI-drafted general nutrition guidance for young cricketers — not individual dietary advice.",
    ask: "For advice about your own needs — allergies, intolerances, medical conditions, or growth — see a registered dietitian or your doctor.",
  },
  fitness: {
    body: "This guide is AI-drafted general fitness guidance for young cricketers — not an individual training programme.",
    ask: "Growing bodies differ. Before starting strength or bowling-workload training, check with a strength & conditioning professional or physiotherapist.",
  },
};

function CategoryDisclosure({ guide }) {
  const d = CATEGORY_DISCLOSURES[guide.category];
  if (!d) return null;
  // The middle sentence is honest about review state: on a published guide
  // it says what the named reviewer's signature MEANS here (general
  // guidance, not advice for you personally); on an admin preview it says
  // that sign-off has not happened yet.
  const reviewed = guide.published
    ? `${guide.reviewerName} reviewed it as general guidance, not as advice for you personally.`
    : "It has not yet been signed off as general guidance.";
  return (
    <div
      className="rounded-xl border-2 p-4 flex gap-3"
      style={{ borderColor: NAVY, background: `${NAVY}08` }}
      data-testid="guide-category-disclosure"
    >
      <Info size={18} color={NAVY} className="shrink-0 mt-0.5" />
      <div className="min-w-0">
        <p className="text-sm font-semibold text-slate-900">General guidance, not advice for you</p>
        <p className="text-sm text-slate-700 mt-1 leading-relaxed max-w-prose">{d.body} {reviewed} {d.ask}</p>
      </div>
    </div>
  );
}

/**
 * #391 — which section types each guide_type's renderer shows OPEN, in the
 * flow of the page, instead of folded into the reference accordion. A
 * fitness drill IS its instructions; a nutrition guide IS its timing
 * phases. Types absent from this map (technique, mindset) keep every
 * section folded, exactly as before.
 */
const OPEN_SECTION_TYPES = {
  "fitness-guide": ["OBJECTIVE"],
  "fitness-drill": ["SETUP", "INSTRUCTIONS", "COACHING_POINTS", "SUCCESS_CRITERIA"],
  nutrition: ["BEFORE_TRAINING", "DURING_TRAINING", "AFTER_TRAINING", "BEFORE_MATCHES", "DURING_MATCHES", "AFTER_MATCHES"],
};

const openSectionTypes = (guide) => new Set(OPEN_SECTION_TYPES[guide.guide_type] || []);

/** One section rendered in the open flow of the page (#391). */
function OpenSection({ section }) {
  return (
    <section data-testid={`guide-open-section-${section.type}`}>
      <div className="flex items-center gap-2 mb-3">
        <ListOrdered size={14} color={NAVY} />
        <h2 className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">{section.heading}</h2>
      </div>
      <div className="space-y-3">
        {section.blocks.map((block, i) => <Block key={i} block={block} />)}
      </div>
    </section>
  );
}

/**
 * #391 — `safety_notes`, the fitness guide's safeguarding field. Spec §3.3
 * made it first-class "precisely because someone already saw this coming":
 * strength and bowling-workload guidance for growing bodies. It renders
 * PROMINENTLY — its own bordered warning card straight after the key theme,
 * never a footnote and never inside a fold.
 */
function SafetyNotes({ notes }) {
  return (
    <section
      className="rounded-xl border-2 p-4 sm:p-5"
      style={{ borderColor: "#B45309", background: "#B4530908" }}
      data-testid="guide-safety-notes"
    >
      <div className="flex items-center gap-2 mb-3">
        <TriangleAlert size={16} className="text-amber-700" />
        <h2 className="text-[11px] uppercase tracking-widest font-semibold" style={{ color: "#92400E" }}>
          Safety notes
        </h2>
      </div>
      <ul className="space-y-2">
        {notes.map((note, i) => (
          <li key={i} className="text-sm text-slate-800 flex gap-2 leading-relaxed" data-testid={`guide-safety-note-${i}`}>
            <TriangleAlert size={14} className="text-amber-600 shrink-0 mt-0.5" />
            {note}
          </li>
        ))}
      </ul>
    </section>
  );
}

/** #391 — `exercises[]`, the repeating child entity (up to 20 per guide)
 * the original card model had no concept of. Numbered cards, open. */
function Exercises({ exercises }) {
  return (
    <section data-testid="guide-exercises">
      <div className="flex items-center gap-2 mb-3">
        <Dumbbell size={14} color={NAVY} />
        <h2 className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Exercises</h2>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
        {exercises.map((e) => (
          <div key={e.number} className="rounded-xl border border-slate-200 p-4" data-testid={`guide-exercise-${e.number}`}>
            <div className="flex items-center gap-2.5 mb-2">
              <span
                className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white shrink-0"
                style={{ background: NAVY }}
              >
                {e.number}
              </span>
              <span className="font-semibold text-sm text-slate-900">{e.name}</span>
            </div>
            <div className="space-y-1.5">
              {e.content.map((text, i) => <Block key={i} block={{ kind: "para", text }} />)}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

/** #391 — nutrition's six timing phases, grouped the way the sport splits
 * them: training days and match days, three phases each, open. */
function NutritionPhases({ guide }) {
  const byType = new Map((guide.sections || []).map((s) => [s.type, s]));
  const groups = [
    { label: "Training days", types: ["BEFORE_TRAINING", "DURING_TRAINING", "AFTER_TRAINING"] },
    { label: "Match days", types: ["BEFORE_MATCHES", "DURING_MATCHES", "AFTER_MATCHES"] },
  ];
  return (
    <div className="space-y-6" data-testid="guide-nutrition-phases">
      {groups.map((group) => (
        <section key={group.label}>
          <div className="flex items-center gap-2 mb-3">
            <Utensils size={14} color={NAVY} />
            <h2 className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">{group.label}</h2>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-2">
            {group.types.map((type) => {
              const section = byType.get(type);
              if (!section) return null;
              return (
                <div key={type} className="rounded-xl border border-slate-200 p-4" data-testid={`guide-phase-${type}`}>
                  <div className="text-[11px] uppercase tracking-widest text-slate-400 mb-2">{section.heading}</div>
                  <div className="space-y-1.5">
                    {section.blocks.map((block, i) => <Block key={i} block={block} />)}
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      ))}
    </div>
  );
}

const FOLD_ITEM_KEYS = (guide) => {
  const keys = [];
  if (guide.featured_professional) keys.push("featured-professional");
  if (guide.technique_breakdown?.length) keys.push("technique-breakdown");
  if (guide.drill?.progressions?.length) keys.push("progressions");
  // #391: a section the type's own renderer shows open never also folds.
  const open = openSectionTypes(guide);
  for (const section of guide.sections || []) {
    if (!open.has(section.type)) keys.push(`section-${section.type}`);
  }
  return keys;
};

/**
 * #1125 — the guide page's Back, in every branch (loaded, not found, load
 * failed, loading). It returns to the hub topic the guide was opened from:
 * the hub's guide links pass `state.from` (`/learn?section=guides&topic=<topic>`
 * since #1128; an old `/learn?tab=<tab>` still resolves on the hub), and a
 * guide opened by URL (no state) falls back to the topic named for its category.
 * `state.from` is data the app was handed, so it goes through
 * `safeRedirectTarget` and must also stay under `/learn`; anything else
 * (another origin, `//`, a backslash, a scheme, another app screen) is
 * ignored.
 */
// The hub's tabs are SURFACES (`LearningHub.jsx`), not categories: a guide's
// category maps to a surface server-side (`CATEGORY_SURFACE`,
// api/src/content/schema/guide.ts), and `facing-bowling` lives on `batting`.
// A loaded guide carries its own `surface`; before it loads (not found,
// failed, loading) this small map stands in. Never name a tab that is not
// one of these: the hub would show a tab row with nothing selected.
const HUB_TABS = ["batting", "bowling", "fielding", "wicketkeeping", "field-placement", "fitness", "nutrition", "mindset"];
// Pinned by api/test/guideHubTabs.pin.test.ts: change `CATEGORY_SURFACE` and this together.
const CATEGORY_TAB = { "facing-bowling": "batting" };
function hubTabFor(category, surface) {
  if (HUB_TABS.includes(surface)) return surface;
  const guess = CATEGORY_TAB[category] || category;
  return HUB_TABS.includes(guess) ? guess : null;
}

// #1128 (D217): the two-section `/learn` names a topic with `?section=guides&topic=`;
// the shipped ten-tab page (flag off, an admin) only knows `?tab=`. Back goes to
// the page that is actually there.
function guideBackTarget(from, category, surface, sections = true) {
  const safe = safeRedirectTarget(from, null);
  if (safe && /^\/learn([/?#]|$)/.test(safe)) return safe;
  const tab = hubTabFor(category, surface);
  if (!tab) return "/learn";
  return sections ? learnTopicHref(tab) : `/learn?tab=${encodeURIComponent(tab)}`;
}

const BACK_STYLE_CLASS = "inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 min-h-[44px]";

function GuideBack({ category, surface }) {
  const location = useLocation();
  const sections = useLearnSectionsLook();
  return (
    <Link
      to={guideBackTarget(location?.state?.from, category, surface, sections)}
      data-testid="guide-back"
      className={BACK_STYLE_CLASS}
    >
      <ArrowLeft size={14} /> Back to Training & guides
    </Link>
  );
}

// Issue #1120: every state of the page (loading, failed, not found, the guide)
// sits under the player's kit bar and ribbon; any other role renders it bare.
export default function GuidePage() {
  return (
    <LearnChrome>
      <GuidePageBody />
    </LearnChrome>
  );
}

function GuidePageBody() {
  const { category, slug } = useParams();
  const [sp, setSp] = useSearchParams();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const reviewMode = isAdmin && sp.get("review") === "1";
  // #481: coach and admin only — a player is never offered the roster call.
  const canAssign = user?.role === "coach" || isAdmin;
  // #1159 slice 3 (D217): the redrawn panel is for a coach on the sections look only;
  // admin and the old look keep the shipped `GuideAssignPanel` below, untouched.
  const sectionsLook = useLearnSectionsLook();
  const coachAssignLook = sectionsLook && user?.role === "coach";

  const [guide, setGuide] = useState(null);
  const [mode, setMode] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [openItems, setOpenItems] = useState([]);
  const [publishing, setPublishing] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  // #1234: focus the page's one h1 on arrival, in every state. Fires on a state
  // change or a new guide, not on an admin reload of the same loaded guide.
  const headingRef = useRef(null);
  const stateKind = notFound ? "not-found" : loadFailed ? "failed" : guide ? "loaded" : "loading";
  // Only when the user has not moved focus themselves: it is on BODY, or still on
  // the heading of the previous state.
  const lastHeading = useRef(null);
  useEffect(() => {
    const el = headingRef.current;
    const active = document.activeElement;
    const untouched = !active || active === document.body || active === lastHeading.current;
    if (el && untouched) el.focus({ preventScroll: true });
    lastHeading.current = el;
  }, [stateKind, category, slug]);

  const load = useCallback(() => {
    let cancelled = false;
    setNotFound(false);
    setLoadFailed(false);
    fetchGuideForPage(category, slug, isAdmin)
      .then(({ guide: g, mode: m }) => {
        if (cancelled) return;
        setGuide(g);
        setMode(m);
        setOpenItems(reviewMode ? FOLD_ITEM_KEYS(g) : []);
      })
      .catch((err) => {
        if (cancelled) return;
        if (err.response?.status === 404) setNotFound(true);
        else setLoadFailed(true);
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category, slug, isAdmin, reviewMode, reloadKey]);

  useEffect(() => load(), [load]);

  const markReviewed = async () => {
    setPublishing(true);
    try {
      await publishGuide(category, slug);
      toast.success(`${guide.title} is now published`);
      setReloadKey((k) => k + 1);
    } catch (err) {
      toast.error("Could not mark this guide as reviewed. Please try again.");
    }
    setPublishing(false);
  };

  if (notFound) {
    return (
      <div className="space-y-4" data-testid="guide-not-found">
        <GuideBack category={category} />
        {/* #1226 — the kit's surface card, not the shipped shadcn `Card`.
            Same copy and testid. */}
        <div
          className="text-center"
          style={{
            padding: 40,
            background: "var(--nw-surface)",
            border: "1px solid var(--nw-line)",
            borderRadius: "var(--nw-radius-card)",
          }}
        >
          <BookOpen size={28} className="mx-auto mb-3" color="var(--nw-ink-muted)" aria-hidden="true" />
          <h1 ref={headingRef} tabIndex={-1} className="nw-h1-as-p nw-focus-target" style={{ color: "var(--nw-ink-muted)" }}>This guide isn't available.</h1>
        </div>
      </div>
    );
  }

  if (loadFailed) {
    return (
      <div className="space-y-4">
        <GuideBack category={category} />
        {/* #1226 — the kit's surface card, not the shipped shadcn `Card`. */}
        <div
          className="flex"
          style={{
            padding: 40,
            background: "var(--nw-surface)",
            border: "1px solid var(--nw-line)",
            borderRadius: "var(--nw-radius-card)",
          }}
        >
          <LoadFailureNote what="this guide" titleAs="h1" titleRef={headingRef} onRetry={() => setReloadKey((k) => k + 1)} testId="guide-load-failed" tapBox={sectionsLook} />
        </div>
      </div>
    );
  }

  // Loading (#1125): the Back and a plain line, never a blank page with no way out.
  if (!guide) {
    return (
      <div className="space-y-4" data-testid="guide-loading">
        <GuideBack category={category} />
        <h1 ref={headingRef} tabIndex={-1} className="nw-h1-as-p nw-focus-target text-sm text-slate-500">Loading…</h1>
      </div>
    );
  }

  const foldKeys = FOLD_ITEM_KEYS(guide);

  return (
    /* #474: the standard page frame, like every other page — no page-wide
       width cap and no centring (the 672px centred ribbon read as a
       rendering fault at desktop widths). The reading measure lives on the
       prose blocks themselves (`max-w-prose`, below), never on a container,
       so structural blocks — card grids, checklists, the pull-quote, the
       reps callout, the accordion — get the full width. The not-found and
       load-failed early returns above already sit uncapped in the same
       frame, so this route now renders ONE width in every state. */
    <div className="space-y-6" data-testid="guide-page">
      <GuideBack category={category} surface={guide.surface} />

      {isAdmin && !guide.published && (
        <div
          className="rounded-xl border-2 p-4 flex flex-col sm:flex-row sm:items-center gap-3"
          style={{ borderColor: GOLD, background: `${GOLD}0F` }}
          data-testid="guide-review-banner"
        >
          <ShieldQuestion size={20} color={GOLD} className="shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-slate-900">Not published — only admins can see this guide.</p>
            <p className="text-xs text-slate-600 mt-0.5">
              {reviewMode
                ? "Every section is force-expanded below. Read all of it before signing off."
                : "Add ?review=1, or open it from the review checklist, to force-expand every section before signing off."}
            </p>
          </div>
          {reviewMode ? (
            <Button
              onClick={markReviewed}
              disabled={publishing}
              className="w-full sm:w-auto shrink-0 h-11 sm:h-9"
              style={{ background: NAVY }}
              data-testid="guide-mark-reviewed-btn"
            >
              <ShieldCheck size={14} className="mr-2" /> {publishing ? "Publishing…" : "Mark as reviewed"}
            </Button>
          ) : (
            <Button
              variant="outline"
              className="w-full sm:w-auto shrink-0 h-11 sm:h-9"
              onClick={() => { const n = new URLSearchParams(sp); n.set("review", "1"); setSp(n, { replace: true }); }}
              data-testid="guide-start-review-btn"
            >
              Review this guide
            </Button>
          )}
        </div>
      )}

      <div>
        <div className="flex items-center gap-2 flex-wrap">
          <Badge variant="outline" className="uppercase tracking-wide text-[10px]">{guide.category.replace(/-/g, " ")}</Badge>
          {guide.published && (
            <span className="text-xs text-slate-500 flex items-center gap-1" data-testid="guide-reviewed-by">
              <ShieldCheck size={12} className="text-emerald-600" />
              Reviewed by {guide.reviewerName} · {fmtWhen(guide.reviewedAt)}
            </span>
          )}
        </div>
        <h1 ref={headingRef} tabIndex={-1} className="nw-focus-target font-display font-black text-3xl text-slate-900 mt-2">{guide.title}</h1>
        {/* #391: a fitness drill's AT A GLANCE pair — the two facts a coach
            checks before picking a drill, as chips under the title. */}
        {guide.at_a_glance && (
          <div className="flex items-center gap-2 mt-2 flex-wrap" data-testid="guide-at-a-glance">
            <Badge variant="outline" className="capitalize">Difficulty: {guide.at_a_glance.difficulty}</Badge>
            <Badge variant="outline">Duration: {guide.at_a_glance.duration}</Badge>
          </div>
        )}
        {/* AI origin disclosed, not headlined (#98's call, unchanged here). */}
        <p className="text-[11px] text-slate-400 mt-1">{guide.provenanceSource}</p>
      </div>

      {/* #391: the category-level disclosure Martin's decision requires —
          ABOVE the content, before anything a player reads, for the three
          affected categories only. */}
      <CategoryDisclosure guide={guide} />

      {/* #481: only on a PUBLISHED guide — the server 404s an assignment
          against an unpublished pair (learnGuides.ts), so an admin
          previewing one must not be offered a button that fails. */}
      {canAssign && guide.published && !coachAssignLook && <GuideAssignPanel category={category} slug={slug} />}

      {/* #105 — the field, drawn, high on the page.
          Its position here is what puts the grade disclaimer above the
          tactical text rather than under it. #105 asks for that disclaimer
          "on the guide pages too, not only on diagrams, because the tactical
          text names close-catching positions whether or not a picture is
          drawn" — and it is satisfied WITHOUT a second copy of the sentence,
          because the schema makes `field_diagram` mandatory for every
          field-placement guide (`guide.ts` superRefine) and the diagram
          carries the disclaimer unconditionally. There is no field-placement
          page without a diagram, so there is none without the disclaimer. */}
      {guide.field_diagram && <FieldDiagram fieldDiagram={guide.field_diagram} />}

      {/* key_theme — a pull-quote, not a paragraph (#220). */}
      <blockquote
        className="rounded-xl border-l-4 p-5 italic text-lg text-slate-800 leading-snug"
        style={{ borderColor: NAVY, background: `${NAVY}06` }}
        data-testid="guide-key-theme"
      >
        “{guide.key_theme}”
      </blockquote>

      {/* #391: a fitness guide leads with what it is FOR, then its safety
          notes — the safeguarding field, prominent and unfoldable — before
          any exercise is shown. */}
      {guide.guide_type === "fitness-guide" && (guide.sections || [])
        .filter((s) => s.type === "OBJECTIVE")
        .map((section) => <OpenSection key={section.type} section={section} />)}
      {guide.safety_notes?.length > 0 && <SafetyNotes notes={guide.safety_notes} />}

      {/* Common mistakes — open by default, the highest-value section on
          the page for a coach watching a player (#220). */}
      {guide.common_mistakes?.length > 0 && (
        <section data-testid="guide-common-mistakes">
          <div className="flex items-center gap-2 mb-3">
            <TriangleAlert size={14} color="#B45309" />
            <h2 className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Common mistakes</h2>
          </div>
          {/* #474: cards, not reading — two-up once the width exists. */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
            {guide.common_mistakes.map((m, i) => (
              <MistakePair key={i} symptom={m.symptom} consequence={m.consequence} testId={`guide-mistake-${i}`} />
            ))}
          </div>
        </section>
      )}

      {/* #391: nutrition's six timing phases, grouped and open — they ARE
          the guide. */}
      {guide.guide_type === "nutrition" && <NutritionPhases guide={guide} />}

      {/* #391: the exercises — a fitness guide's repeating child entity,
          numbered and open. */}
      {guide.exercises?.length > 0 && <Exercises exercises={guide.exercises} />}

      {/* #391: a fitness drill's own document sections — setup,
          instructions, coaching points, success criteria — open, in source
          order. The document IS the drill. */}
      {guide.guide_type === "fitness-drill" && (guide.sections || [])
        .filter((s) => openSectionTypes(guide).has(s.type))
        .map((section) => <OpenSection key={section.type} section={section} />)}

      {/* The drill — purpose, setup, equipment, reps, coaching points and
          common errors, open by default (#220: "the whole drill block"). */}
      {guide.drill && (
        <section className="space-y-4" data-testid="guide-drill">
          <div className="flex items-center gap-2">
            <Target size={14} color={NAVY} />
            <h2 className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Practice drill</h2>
          </div>

          <p className="text-sm text-slate-700 leading-relaxed max-w-prose">{guide.drill.purpose}</p>

          {/* reps — "the thing you glance at mid-session" (#220): its own
              prominent, single-line callout, not folded into prose. */}
          <div
            className="rounded-xl px-4 py-3 flex items-center gap-2 font-semibold"
            style={coachAssignLook
              // D344: on the coach's page the Assign button is the one gold; the chip is content and yields.
              ? { background: "var(--nw-line-faint)", color: "var(--nw-ink-lede)" }
              : { background: `${GOLD}15`, color: "#7C5A08" }}
            data-testid="guide-drill-reps"
          >
            <Clock size={16} className="shrink-0" />
            {guide.drill.reps}
          </div>

          <p className="text-sm text-slate-600 leading-relaxed max-w-prose">{guide.drill.setup}</p>

          {/* equipment — a checklist, someone is packing a bag (#220). */}
          <div>
            <div className="text-[11px] uppercase tracking-widest text-slate-400 mb-1.5">Equipment</div>
            {/* #474: a checklist packs a bag — 3-up at lg, not squeezed. */}
            <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-1.5" data-testid="guide-drill-equipment">
              {guide.drill.equipment.map((item, i) => (
                <li key={i} className="text-sm text-slate-700 flex items-center gap-2">
                  <CheckSquare size={14} className="text-slate-400 shrink-0" /> {item}
                </li>
              ))}
            </ul>
          </div>

          {/* coaching_points — short cues, generously spaced, read
              one-handed outdoors in sunlight (#220). */}
          <div>
            <div className="text-[11px] uppercase tracking-widest text-slate-400 mb-1.5">Coaching points</div>
            <ul className="space-y-2.5" data-testid="guide-drill-coaching-points">
              {guide.drill.coaching_points.map((point, i) => (
                <li key={i} className="text-sm font-medium text-slate-800 flex gap-2.5">
                  <Sparkles size={14} className="text-slate-400 shrink-0 mt-0.5" /> {point}
                </li>
              ))}
            </ul>
          </div>

          {guide.drill.common_errors?.length > 0 && (
            <div>
              <div className="text-[11px] uppercase tracking-widest text-slate-400 mb-1.5">Common errors in the drill</div>
              {/* #474: same call as common mistakes above — cards go two-up at lg. */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-2" data-testid="guide-drill-common-errors">
                {guide.drill.common_errors.map((e, i) => (
                  <ErrorTriple key={i} failure={e.failure} why={e.why} correction={e.correction} testId={`guide-drill-error-${i}`} />
                ))}
              </div>
            </div>
          )}
        </section>
      )}

      {/* Folded reference reading (#220 / spec §2.1): featured professional,
          technique breakdown, drill progressions, then any remaining
          corpus sections this guide's type carries. Force-expanded
          (`openItems` seeded to every key) in review mode — #98/#219's
          rule that a bad cue must not be able to survive a fold. */}
      {foldKeys.length > 0 && (
        <Accordion type="multiple" value={openItems} onValueChange={setOpenItems} data-testid="guide-accordion">
          {guide.featured_professional && (
            <AccordionItem value="featured-professional" data-testid="guide-fold-featured-professional">
              <AccordionTrigger data-testid="guide-accordion-trigger-featured-professional">
                <span className="flex items-center gap-2"><Star size={14} color={GOLD} /> Featured professional</span>
              </AccordionTrigger>
              <AccordionContent>
                {guide.featured_professional.name && (
                  <p className="font-semibold text-slate-900 mb-1">{guide.featured_professional.name}</p>
                )}
                <p className="text-sm text-slate-700 leading-relaxed max-w-prose">{guide.featured_professional.paragraph}</p>
              </AccordionContent>
            </AccordionItem>
          )}

          {guide.technique_breakdown?.length > 0 && (
            <AccordionItem value="technique-breakdown" data-testid="guide-fold-technique-breakdown">
              <AccordionTrigger data-testid="guide-accordion-trigger-technique-breakdown">Technique breakdown</AccordionTrigger>
              <AccordionContent className="space-y-3">
                {guide.technique_breakdown.map((para, i) => (
                  <p key={i} className="text-sm text-slate-700 leading-relaxed max-w-prose">{para}</p>
                ))}
              </AccordionContent>
            </AccordionItem>
          )}

          {guide.drill?.progressions?.length > 0 && (
            <AccordionItem value="progressions" data-testid="guide-fold-progressions">
              <AccordionTrigger data-testid="guide-accordion-trigger-progressions">Drill progressions</AccordionTrigger>
              <AccordionContent>
                <ol className="space-y-1.5 list-decimal list-inside">
                  {guide.drill.progressions.map((p, i) => (
                    <li key={i} className="text-sm text-slate-700 leading-relaxed">{p}</li>
                  ))}
                </ol>
              </AccordionContent>
            </AccordionItem>
          )}

          {/* #391: a section the type's renderer already shows open never
              also folds — same filter as FOLD_ITEM_KEYS. */}
          {(guide.sections || [])
            .filter((section) => !openSectionTypes(guide).has(section.type))
            .map((section) => (
            <AccordionItem key={section.type} value={`section-${section.type}`} data-testid={`guide-fold-section-${section.type}`}>
              <AccordionTrigger data-testid={`guide-accordion-trigger-section-${section.type}`}>{section.heading}</AccordionTrigger>
              <AccordionContent className="space-y-3">
                {section.blocks.map((block, i) => <Block key={i} block={block} />)}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      )}

      {/* #1159 slice 3 (D259): the board draws the guide body first and Assign this guide after it, for the
          coach on the new look. Admin and the old look keep the shipped panel above the body. */}
      {canAssign && guide.published && coachAssignLook && <CoachGuideAssignPanel category={category} slug={slug} />}
    </div>
  );
}
